import { AlertTriangle, DatabaseZap, LogOut, RotateCcw, ShieldX } from 'lucide-react'
import { type ReactNode } from 'react'

import { Wordmark } from '@/components/layout/Wordmark'
import { Button } from '@/components/ui/Button'
import { useT } from '@/i18n'

import { useAdminSession } from './session'

/**
 * The dead end, rendered IN PLACE.
 *
 * This screen exists because of the two failure modes named in `session.ts`, and
 * the way it avoids them is by not navigating at all:
 *
 *   • A REDIRECT LOOP. If "not an admin" sent the operator to `/login`, the
 *     login page would see a valid session, send them back, and the browser
 *     would ping-pong until it gave up. So `denied` is terminal: whatever route
 *     the operator asked for, this renders there, and the URL stops moving.
 *   • A BLANK PAGE. Every non-authorized outcome lands here with a heading, a
 *     cause and a next action — never an empty `<div>` and never a silent
 *     `return null`.
 *
 * The three causes look identical from the browser and must not be collapsed:
 * "you are signed in and not an admin", "the check could not be run at all", and
 * "this build has no Supabase credentials" need three different actions from
 * three different people.
 */
export function NoAccessPage(): ReactNode {
  const t = useT()
  const { status, email, deniedReason, errorMessage, recheck, signOut } = useAdminSession()

  const kind =
    status === 'unconfigured'
      ? 'unconfigured'
      : deniedReason === 'check-failed'
        ? 'check-failed'
        : 'not-admin'

  const Icon = kind === 'not-admin' ? ShieldX : kind === 'check-failed' ? AlertTriangle : DatabaseZap

  return (
    <main className="flex min-h-svh items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Wordmark />
        </div>

        <div className="rounded-lg border border-border bg-card p-5 shadow-2 sm:p-6">
          <span
            aria-hidden="true"
            className="flex size-10 items-center justify-center rounded-full bg-destructive-subtle text-destructive-subtle-foreground"
          >
            <Icon className="size-5" />
          </span>

          <h1 className="mt-4 type-h3 text-foreground">{t(`noAccess.${kind}.title`)}</h1>
          <p className="mt-1.5 text-body-sm text-muted-foreground">
            {t(`noAccess.${kind}.body`)}
          </p>

          {/* WHICH account was refused. Without it an operator with two logins
              cannot tell whether they simply signed in as the wrong one. */}
          {email ? (
            <p className="mt-4 rounded-md border border-border bg-muted/60 p-3 text-body-sm">
              <span className="text-muted-foreground">{t('noAccess.signedInAs')} </span>
              <span className="font-medium text-foreground">{email}</span>
            </p>
          ) : null}

          {/*
            ⚠️ THE EXPECTED ANSWER TODAY, and the reason this paragraph is not a
            developer comment. `public.is_admin()` opens with

                if to_regclass('public.admin_users') is null then return false;

            and `admin_users` has not been created yet — it is migration 014
            onward, with the rest of the admin tables. So a correct password on a
            real account lands here, and from the browser that is indistinguishable
            from "your access was revoked". An operator must not be left to guess
            which, so the panel says it out loud.
          */}
          {kind === 'not-admin' ? (
            <p className="mt-3 rounded-md border border-warning/40 bg-warning-subtle p-3 text-caption text-warning-subtle-foreground">
              {t('noAccess.notAdmin.pending')}
            </p>
          ) : null}

          {errorMessage ? (
            <p className="mt-3 break-words rounded-md border border-border bg-muted/60 p-3 font-mono text-caption text-muted-foreground">
              {errorMessage}
            </p>
          ) : null}

          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            {/* Retry first for `check-failed`: a network blink is the one cause
                here that fixes itself, and re-running the check is cheaper than
                signing out and back in. */}
            <Button
              variant={kind === 'check-failed' ? 'primary' : 'outline'}
              className="w-full sm:w-auto"
              onClick={recheck}
              icon={<RotateCcw aria-hidden="true" />}
            >
              {t('noAccess.retry')}
            </Button>

            {/* The only way out of a terminal state — and, on a shared machine,
                the thing that has to be one click away. Hidden when there is no
                session to end. */}
            {status !== 'unconfigured' ? (
              <Button
                variant={kind === 'not-admin' ? 'primary' : 'outline'}
                className="w-full sm:w-auto"
                onClick={() => void signOut()}
                icon={<LogOut aria-hidden="true" />}
              >
                {t('noAccess.signOut')}
              </Button>
            ) : null}
          </div>
        </div>

        <p className="mt-4 text-center text-caption text-muted-foreground">
          {t('auth.restricted')}
        </p>
      </div>
    </main>
  )
}

export default NoAccessPage
